import React, { useMemo, useState } from 'react'
import ReactDataGrid from 'react-data-grid'
import styles from './CanvasLab.module.scss'

function cellText(value) {
  if (value == null) return ''
  if (value instanceof Date) return value.toISOString()
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

function compareRows(left, right, column, type) {
  const a = left.row[column]
  const b = right.row[column]
  if (type === 'numeric') return (Number(a) || 0) - (Number(b) || 0)
  if (type === 'date') return new Date(a).valueOf() - new Date(b).valueOf()
  return cellText(a).localeCompare(cellText(b), undefined, { numeric: true })
}

// The production data editor uses this same virtualized grid package, but the
// Canvas explorer intentionally remains read-only. It is a quick way to
// understand a loaded dataset, not a second data-cleaning surface.
export default function CanvasDataExplorer({ dataset, columns, audit }) {
  const [query, setQuery] = useState('')
  const [queryColumn, setQueryColumn] = useState('')
  const [sortColumn, setSortColumn] = useState('_row')
  const [sortDirection, setSortDirection] = useState('NONE')

  const rows = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase()
    const searchableColumns = queryColumn ? [queryColumn] : columns
    const filtered = (dataset.rows || [])
      .map((row, index) => ({ row, _row: index + 1 }))
      .filter(({ row }) => !normalizedQuery || searchableColumns.some((column) => (
        cellText(row[column]).toLocaleLowerCase().includes(normalizedQuery)
      )))
    if (sortDirection === 'NONE') return filtered
    const type = sortColumn === '_row' ? 'numeric' : audit?.schema?.[sortColumn]?.type
    return [...filtered].sort((left, right) => {
      const comparison = sortColumn === '_row'
        ? left._row - right._row
        : compareRows(left, right, sortColumn, type)
      return sortDirection === 'DESC' ? -comparison : comparison
    })
  }, [audit, columns, dataset.rows, query, queryColumn, sortColumn, sortDirection])

  const gridColumns = useMemo(() => [
    { key: '_row', name: '#', width: 58, frozen: true, sortable: true },
    ...columns.map((column) => ({
      key: column,
      name: column,
      sortable: true,
      resizable: true,
      formatter: ({ row }) => <span title={cellText(row.row[column])}>{cellText(row.row[column])}</span>,
    })),
  ], [columns])

  const gridHeight = Math.min(440, Math.max(170, rows.length * 34 + 38))

  return <div className={styles.dataExplorer}>
    <div className={styles.dataExplorerToolbar}>
      <select aria-label="Search column" value={queryColumn} onChange={(event) => setQueryColumn(event.target.value)}>
        <option value="">All columns</option>
        {columns.map((column) => <option key={column} value={column}>{column}</option>)}
      </select>
      <input aria-label="Search data" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search values" />
      <span>{rows.length.toLocaleString()} of {(dataset.rows || []).length.toLocaleString()} rows</span>
    </div>
    <div className={styles.dataExplorerSchema} aria-label="Column profiles">
      {columns.map((column) => {
        const info = audit?.schema?.[column] || {}
        return <div key={column}>
          <strong>{column}</strong>
          <span>{info.type || 'unknown'} · {(info.uniqueValues || 0).toLocaleString()} unique{info.missingCount ? ` · ${info.missingCount.toLocaleString()} blanks` : ''}{info.likelyMeasure ? ' · measure' : ''}</span>
        </div>
      })}
    </div>
    <div className={styles.dataExplorerGrid}>
      <ReactDataGrid
        columns={gridColumns}
        rows={rows}
        rowHeight={34}
        height={gridHeight}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={(column, direction) => { setSortColumn(column); setSortDirection(direction) }}
      />
    </div>
  </div>
}
